import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const MyTeamDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/my-team/dashboard');
        if (!cancelled) setData(r.data?.data || null);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.message || 'Failed to load dashboard');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Could not load team dashboard.</Alert>
      </Box>
    );
  }

  if (!data.reportee_count) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          My Team Dashboard
        </Typography>
        <Alert severity="info">You have no direct reports to show team metrics for.</Alert>
      </Box>
    );
  }

  const { totals, by_member, submissions_by_status, recent_team_submissions } = data;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        My Team Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Jobs your team touches (as owners or creators), submissions on those jobs or filed by your reports, and
        per-person activity.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Active jobs (team)
              </Typography>
              <Typography variant="h4">{totals.active_jobs ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Requirements touched
              </Typography>
              <Typography variant="h4">{totals.job_requirements_touched ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Submissions (team scope)
              </Typography>
              <Typography variant="h4">{totals.submissions_total ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                In interview pipeline
              </Typography>
              <Typography variant="h4">{totals.candidates_in_interview_pipeline ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                scheduled + interviewed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Per team member
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Jobs created</TableCell>
              <TableCell align="right">Jobs as owner</TableCell>
              <TableCell align="right">Submissions</TableCell>
              <TableCell align="right">Their candidates in interview</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(by_member || []).map((row) => (
              <TableRow key={row.employee_id}>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">{row.jobs_owned_created}</TableCell>
                <TableCell align="right">{row.jobs_as_requirement_owner}</TableCell>
                <TableCell align="right">{row.submissions_count}</TableCell>
                <TableCell align="right">{row.candidates_in_interview_pipeline}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Submission status (team scope)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {(submissions_by_status || []).map((s) => (
          <Chip key={s.status} label={`${s.status}: ${s.count}`} variant="outlined" />
        ))}
        {!submissions_by_status?.length && (
          <Typography variant="body2" color="text.secondary">
            No submissions in scope.
          </Typography>
        )}
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Recent submissions
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job</TableCell>
              <TableCell>Candidate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(recent_team_submissions || []).map((s) => {
              const job = s.job_requirement || s.jobRequirement;
              return (
              <TableRow key={s.id}>
                <TableCell>
                  {job?.job_code} — {job?.title}
                </TableCell>
                <TableCell>
                  {s.candidate
                    ? `${s.candidate.first_name || ''} ${s.candidate.last_name || ''}`.trim() || s.candidate.email
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={s.status} />
                </TableCell>
                <TableCell>{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MyTeamDashboard;
